import React, {type ComponentProps, type ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import {type PopoverOverlay} from '@time-webkit/all/molecules/popover';
import {useEventCallback} from 'usehooks-ts';

import classNames from 'classnames';

import {DotsHorizontalIcon} from '@time-webkit/all/icons/dots-horizontal';

import {HoverPopover} from '@time-webkit/all/molecules/hover-popover';

import {Divider} from '../divider';
import {ActionButton} from '../action-button';

import styles from './styles.module.css';

interface OverflowContainerProps {
    className?: string;
    menuClassName?: string;
    hiddenNodes?: ReactNode;
    children?: ReactNode;
    popoverTrigger?: React.RefObject<HTMLElement>;
    placement?: ComponentProps<typeof PopoverOverlay>['placement'];
    onClose?: (event: MouseEvent) => void;
}

const isDivider = (element: ReactNode): boolean => {
    return React.isValidElement(element) && element.type === Divider;
};

const withFakeNode = (children: ReactNode) =>
    React.Children.map(children, (child) => {
        if (React.isValidElement(child) && !(child as React.ReactPortal).key && !isDivider(child)) {
            return <div className={styles.fakeElement} />;
        }
        return child;
    });

export const OverflowContainer: React.FC<OverflowContainerProps> = (props) => {
    const {
        children,
        hiddenNodes,
        className,
        menuClassName,
        popoverTrigger,
        placement = 'top',
        onClose,
        ...restProps
    } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const fakeMoreActionsRef = React.useRef(null);
    const moreActionsRef = React.useRef<HTMLButtonElement>(null);
    const prevContainerWidth = useRef(0);

    const [visibleItems, setVisibleItems] = useState<ReactNode[]>([]);
    const [dropdownItems, setDropdownItems] = useState<ReactNode[]>([]);
    const [itemRightSides, setItemRightSides] = useState<number[]>([]);
    const [isActive, setIsActive] = useState(false);

    const rendererRef = useCallback((node: HTMLDivElement) => {
        if (node) {
            const sides = [];

            const rendererStyles = window.getComputedStyle(node);
            const rendererGap = parseInt(rendererStyles.gap, 10);
            const rendererPadding = parseInt(rendererStyles.paddingLeft, 10);
            for (let i = 0; i < node.children.length - 1; i++) {
                const child = node.children[i + 1] as HTMLElement | undefined;
                if (!child) {
                    continue;
                }

                const rightEdge = child.offsetLeft - rendererGap - rendererPadding;
                sides.push(rightEdge);
            }
            setItemRightSides(sides);
        }
    }, []);

    const updateItems = useCallback(() => {
        if (!containerRef.current || itemRightSides.length === 0) {
            return;
        }

        const moreActionsWidth = fakeMoreActionsRef.current?.offsetWidth || 0;
        const containerStyles = window.getComputedStyle(containerRef.current);
        const containerGap = parseInt(containerStyles.gap, 10);

        const containerWidth = containerRef.current.clientWidth -
            parseInt(containerStyles.paddingLeft, 10) -
            parseInt(containerStyles.paddingRight, 10) -
            (hiddenNodes ? moreActionsWidth + containerGap : 0);

        let visibleCount;
        for (visibleCount = 0; visibleCount < itemRightSides.length; visibleCount++) {
            const side = itemRightSides[visibleCount] || 0;
            if (side >= containerWidth) {
                break;
            }
        }

        const nodes = React.Children.toArray(children);
        let dropdownCount = nodes.length - visibleCount;

        if (!hiddenNodes && dropdownCount > 0) {
            if (dropdownCount === 1) {
                dropdownCount++;
                visibleCount--;
            }

            if (dropdownCount === 2 && isDivider(nodes.at(-dropdownCount))) {
                dropdownCount++;
                visibleCount--;
            }

            const widthWithoutMoreAction = containerWidth - moreActionsWidth - containerGap;
            for (let i = visibleCount - 1; i >= 0; i--) {
                const side = itemRightSides[i] || 0;
                if (side >= widthWithoutMoreAction) {
                    dropdownCount++;
                    visibleCount--;
                } else {
                    break;
                }
            }
        }

        if (isDivider(nodes.at(-dropdownCount))) {
            dropdownCount--;
        }

        const visibleChildren = nodes.slice(0, visibleCount);
        const dropdownChildren = nodes.slice(nodes.length - dropdownCount);

        setVisibleItems(visibleChildren);
        setDropdownItems(dropdownChildren);
    }, [children, hiddenNodes, itemRightSides]);

    const handleResize = useEventCallback((entries: ResizeObserverEntry[]) => {
        for (const entry of entries) {
            if (entry.target === containerRef.current) {
                const containerWidth = entry.contentBoxSize[0]?.inlineSize;
                if (prevContainerWidth.current !== containerWidth) {
                    updateItems();
                }
                prevContainerWidth.current = containerWidth || 0;
            }
        }
    });

    useEffect(() => {
        const resizeObserver = new ResizeObserver(handleResize);

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        updateItems();

        return () => resizeObserver.disconnect();
    }, [handleResize, updateItems]);

    const handleOpen = () => {
        setIsActive(true);
    };

    const handleClose = (event: MouseEvent) => {
        setIsActive(false);
        onClose?.(event);
    };

    return (
        <>
            <div ref={containerRef} className={className} {...restProps}>
                {visibleItems}
                {(dropdownItems.length > 0 || hiddenNodes) &&
                    <HoverPopover
                        OverlayComponent={
                            <div className={classNames(styles.menu, menuClassName)}>
                                {dropdownItems}
                                {hiddenNodes}
                            </div>
                        }
                        showDelay={500}
                        hideDelay={800}
                        placement={placement}
                        offset={6}
                        positionTargetRef={popoverTrigger || moreActionsRef}
                        isHoverDisabled={true}
                        onOpen={handleOpen}
                        onClose={handleClose}
                    >
                        <ActionButton
                            ref={moreActionsRef}
                            icon={<DotsHorizontalIcon width={24} />}
                            isActive={isActive}
                        />
                    </HoverPopover>
                }
            </div>
            <div className={styles.rendererContainer}>
                <div ref={rendererRef} className={classNames(styles.renderer, className)}>
                    {withFakeNode(children)}
                    <div className={styles.fakeElement} ref={fakeMoreActionsRef} />
                </div>
            </div>
        </>
    );
};
